import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Button, Icon } from "@/shared/ui";
import { SelectScreenCardTypes } from "@/assets/constants/selectScreenCardTypes";

type Props = {
  type: SelectScreenCardTypes;
  level?: string;
  idInfo?: string;
  address?: string;
  onChangeSelected?: () => void;
  cardImage?: string;
  badge?: ReactNode;
  maxCards?: number;
  logoImage?: string;
  levelIcon?: ReactNode;
  CardIcon?: (props: { className?: string }) => JSX.Element;
  isGrid?: boolean;
};

export default function SelectScreenCard({
  type,
  level,
  idInfo,
  address,
  onChangeSelected,
  cardImage,
  badge,
  maxCards,
  logoImage,
  levelIcon,
  CardIcon,
  isGrid,
}: Props) {
  const { t } = useTranslation("tablet");

  return (
    <div
      className="relative flex justify-center items-center bg-dark-800/30 border border-dashed border-[#1C1C1C] rounded-2xl overflow-hidden"
      style={{ flexBasis: !isGrid && `${100 / maxCards}%` }}
    >
      {type === SelectScreenCardTypes.Purchased && (
        <button
          className="relative flex flex-col justify-end size-full"
          onClick={onChangeSelected}
          type="button"
        >
          <div
            className={clsx(
              "absolute top-0 left-0 w-full bg-cover bg-no-repeat bg-center",
              isGrid ? "h-full" : "h-[26.5rem]",
            )}
            style={{ backgroundImage: cardImage && `url(${cardImage})` }}
          />
          {badge && badge}
          <div
            className={clsx(
              "relative z-1 flex justify-between items-end w-full p-7.5",
              isGrid && "h-full",
            )}
          >
            <div className="flex flex-col items-start">
              <div className="flex self-start items-center gap-1 mb-2.5 p-2 pr-[0.4375rem] bg-white rounded-[0.3125rem]">
                {levelIcon || (
                  <Icon category="home-menu" name="star" className="bg-dark-800 w-3.5" />
                )}
                <p className="text-12 text-dark-800 font-bold uppercase">{level}</p>
              </div>
              <p className="text-18 font-bold">{idInfo}</p>
              <p className="text-15 font-medium text-white/50">{address}</p>
            </div>
            {logoImage && (
              <div
                className={clsx(
                  "w-[8.375rem] h-7.5 bg-contain bg-no-repeat bg-center",
                  isGrid && "absolute top-7.5 left-7.5",
                )}
                style={{ backgroundImage: `url(${logoImage})` }}
              />
            )}
          </div>
          <span
            className={clsx("absolute bottom-0 left-0 w-full", isGrid ? "h-32" : "h-60")}
            style={{
              background:
                "linear-gradient(0deg, #181818 0%, #181818 30%, rgba(24, 24, 24, 0.90) 60%,  rgba(24, 24, 24, 0.6) 80%, rgba(24, 24, 24, 0.2) 90%, transparent 100%)",
            }}
          />
        </button>
      )}
      {type === SelectScreenCardTypes.Empty && (
        <div className="flex flex-col justify-center items-center gap-3.5">
          {CardIcon && <CardIcon className="size-7.5 text-white/10" />}
          <p className="text-10 font-bold text-white/30 uppercase">
            {t("selectScreen.placeEmpty")}
          </p>
        </div>
      )}
      {type === SelectScreenCardTypes.Blocked && (
        <>
          <div className="flex flex-col justify-center items-center gap-3.5">
            <Icon category="home-menu" name="locked-2" className="bg-white/10 w-7" />
            <p className="text-10 font-bold text-white/30 uppercase">
              {t("selectScreen.unlockSlot")}
            </p>
          </div>
          <Button className="absolute left-1/2 bottom-7.5 w-[calc(100%-3.75rem)] h-11 -translate-x-[50%]">
            {t("selectScreen.unlock")}
          </Button>
        </>
      )}
    </div>
  );
}
